import type { PaginationType } from '../types/Pagination.types';
import { z } from 'zod';
import { MediaDtoSchema } from '../media/media.schema';
import { createUpdateZodSchema } from '../types/DateUtils.types';
import { PaginationParamsSchema } from '../types/Pagination.types';

export const GamificationRewardType = {
  BirthdayPresent: 'birthday-present',
  FreeSpins: 'free-spins',
  LevelUp: 'level-up',
  LossInsurance: 'loss-insurance',
  MonthlyPlayback: 'monthly-playback',
  Referral: 'referral',
  StatusUp: 'status-up',
  WeeklyPlayback: 'weekly-playback',
  WinnersPool: 'winners-pool',
} as const;

export const RewardTypeSchema = z.nativeEnum(GamificationRewardType);

const StatusUpRewardConfigSchema = z.object({
  statusId: z.string().uuid(),
});

const LevelUpRewardConfigSchema = z.object({});

export const GamificationRewardConfigSchema = z.union([
  StatusUpRewardConfigSchema,
  LevelUpRewardConfigSchema,
]);

export const RewardPayoutSchema = z.object({
  sweepCoin: z.number(),
  goldCoin: z.number(),
  bonusSweepCoin: z.number(),
}).describe('RewardPayoutSchema');

export const RewardAssetDtoSchema = z.object({
  id: z.string().uuid(),
  rewardId: z.string().uuid(),
  mediaId: z.string().uuid(),
  media: MediaDtoSchema,
  appearance: z.string(),
}).merge(createUpdateZodSchema).describe('RewardAssetDtoSchema');

export const RewardDtoSchema = z.object({
  id: z.string().uuid(),
  type: RewardTypeSchema,
  levelFrom: z.number(),
  isActive: z.boolean(),
  config: GamificationRewardConfigSchema,
  presentation: z.record(z.string(), z.unknown()).nullish(),
  assets: z.array(RewardAssetDtoSchema).default([]),
}).merge(createUpdateZodSchema).describe('RewardDtoSchema');

export const PlayerRewardDtoSchema = z.object({
  id: z.string().uuid(),
  rewardId: z.string().uuid(),
  reward: RewardDtoSchema,
  config: GamificationRewardConfigSchema,
  claimedAt: z.string().datetime().nullable(),
}).merge(createUpdateZodSchema).describe('PlayerRewardDtoSchema');

export const ClaimedPlayerRewardDtoScema = PlayerRewardDtoSchema.merge(z.object({
  payout: RewardPayoutSchema,
})).describe('ClaimedPlayerRewardDtoScema');

export const GroupedPlayerRewardDtoSchema = z.object({
  type: RewardTypeSchema,
  availableAmount: z.number(),
  minLevelFrom: z.number().nullable(),
  presentation: z.record(z.string(), z.unknown()).nullish(),
  assets: z.array(z.unknown()).default([]),
}).describe('GroupedPlayerRewardDtoSchema');

export const GroupedPlayerRewardListDtoSchema = z
  .array(GroupedPlayerRewardDtoSchema)
  .describe('GroupedPlayerRewardListDtoSchema');

const RewardTypeFilterSchema = z.union([
  RewardTypeSchema,
  z.object({
    $in: z.array(RewardTypeSchema).optional(),
  }),
]);

const ClaimedAtFilterSchema = z.object({
  $isnull: z.literal(true).optional(),
  $notnull: z.literal(true).optional(),
});

export const PlayerRewardFiltersSchema = z.object({
  type: RewardTypeFilterSchema.optional(),
  levelFrom: z.number().optional(),
  claimedAt: ClaimedAtFilterSchema.optional(),
}).optional();

const SortOrderSchema = z.enum(['asc', 'desc']);

export const PlayerRewardParamsSchema = PaginationParamsSchema(PlayerRewardFiltersSchema).extend({
  sort: z.object({
    levelFrom: SortOrderSchema.optional(),
    createdAt: SortOrderSchema.optional(),
  }).optional(),
  filter: PlayerRewardFiltersSchema,
});

export type RewardTypeValue = z.infer<typeof RewardTypeSchema>;
export type RewardConfig = z.infer<typeof GamificationRewardConfigSchema>;
export type RewardAssetDto = z.infer<typeof RewardAssetDtoSchema>;
export type RewardDto = z.infer<typeof RewardDtoSchema>;
export type PlayerRewardDto = z.infer<typeof PlayerRewardDtoSchema>;
export type PlayerRewardListDto = PaginationType<PlayerRewardDto>;
export type GroupedPlayerRewardDto = z.infer<typeof GroupedPlayerRewardDtoSchema>;
export type ClaimedPlayerRewardDto = z.infer<typeof ClaimedPlayerRewardDtoScema>;
export type PlayerRewardParams = z.infer<typeof PlayerRewardParamsSchema>;
